import { useState } from "react";
import { UserModel } from "../../app/entities";
import { UserTypes, UserTypesCodes } from "./constants";
import { ObjectToQueryParams } from "@/utils";
import { useAuthStore } from "@/context";
import useApi from "@/services/use.api";
import { UserApiResponse, useUserStore } from "@/context/stores/user.store";
import { useToast } from "../toast";

const { useGetApi, usePostApi, usePutApi, useDeleteApi } = useApi();

export type FetchUsersParams = {
  typeFetch?: string;
  type?: UserTypes;
  page?: number;
  size?: number;
};

export const useUsers = () => {
  const { saving, users, loading, setSaving, setLoading, setUsers } =
    useUserStore();

  const [currentType, setCurrentType] = useState<UserTypes>(
    UserTypes.PARTICULIER
  );
  const [availableTypes, setAvailableTypes] = useState<UserTypes[]>([]);
  const { currentUser: user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const getUserTypesOptions = () => {
    switch (user?.type) {
      case UserTypes.ADMINISTRATEUR:
        return [
          { value: "", label: "Choisissez un type" },
          { value: UserTypes.PARTICULIER, label: "Particulier" },
          { value: UserTypes.TECHNIQUE, label: "Technique" },
          { value: UserTypes.ADMINISTRATEUR, label: "Admin" },
          { value: UserTypes.TERRAIN, label: "Terrain" },
        ];
      case UserTypes.TECHNIQUE:
        return [
          { value: "", label: "Choisissez un type" },
          { value: UserTypes.PARTICULIER, label: "Particulier" },
          { value: UserTypes.TECHNIQUE, label: "Technique" },
          { value: UserTypes.TERRAIN, label: "Terrain" },
        ];
      case UserTypes.TERRAIN:
        return [
          { value: "", label: "Choisissez un type" },
          { value: UserTypes.PARTICULIER, label: "Particulier" },
        ];
      default:
        return [{ value: "", label: "Choisissez un type" }];
    }
  };

  const getTypeToFetch = (): UserTypesCodes => {
    switch (user?.type) {
      case UserTypes.ADMINISTRATEUR:
        return UserTypesCodes.Admin;
      case UserTypes.TECHNIQUE:
        return UserTypesCodes.Technique;
      case UserTypes.TERRAIN:
        return UserTypesCodes.Terrain;
      default:
        return UserTypesCodes.Common;
    }
  };

  const getAvailableTypes = (): UserTypes[] => {
    switch (user?.type) {
      case UserTypes.ADMINISTRATEUR:
        return Object.values(UserTypes).filter(
          (t) => t !== UserTypes.ADMINISTRATEUR
        );
        break;
      case UserTypes.TECHNIQUE:
        return Object.values(UserTypes).filter(
          (t) => t !== UserTypes.ADMINISTRATEUR && t !== UserTypes.TECHNIQUE
        );
      case UserTypes.TERRAIN:
        return Object.values(UserTypes).filter(
          (t) =>
            t !== UserTypes.ADMINISTRATEUR &&
            t !== UserTypes.TECHNIQUE &&
            t !== UserTypes.TERRAIN
        );
      default:
        return [];
    }
  };

  const fetchUsers = async (params?: FetchUsersParams) => {
    setLoading(true);
    const param = ObjectToQueryParams(params);
    const uri = params?.type
      ? `/auth/utilisateur/${getTypeToFetch()}/type/${params.type}${param}`
      : `/auth/utilisateur/${getTypeToFetch()}${param}`;
    const res = await useGetApi<UserApiResponse>(uri);
    const { data } = res ?? {};

    if (data) {
      setUsers(data);
    }

    setLoading(false);
  };

  const getUsers = async (params: FetchUsersParams): Promise<UserModel[]> => {
    setLoading(true);
    const param = ObjectToQueryParams(params);
    const uri = params?.type
      ? `/auth/utilisateur/${params?.typeFetch || getTypeToFetch()}/type/${
          params.type
        }${param}`
      : `/auth/utilisateur/${params?.typeFetch || getTypeToFetch()}${param}`;
    const res = await useGetApi<UserApiResponse>(uri);
    const { data } = res ?? {};
    setLoading(false);

    return (data.content || []) as UserModel[];
  };

  const saveUser = async (params: any): Promise<any> => {
    setSaving(true);
    if (!user || !getAvailableTypes().includes(params?.type as UserTypes)) {
      useToast("Utilisateur non connecté ou type non autorisé", {
        variant: "error",
      });
      return;
    }

    const res = await usePostApi(
      `/auth/utilisateur/${getTypeToFetch()}`,
      params
    );

    const { success, message, data } = res ?? {};
    if (!success) {
      useToast(message ?? "Erreur lors de l'ajout de l'utilisateur", {
        variant: "error",
      });
      setSaving(false);
      return {
        success: false,
        message: message ?? "Erreur lors de l'ajout de l'utilisateur",
      };
    }

    useToast("Utilisateur ajouté avec succès", {
      variant: "success",
    });
    setSaving(false);
    return {
      success: true,
      message: "Utilisateur ajouté avec succès",
    };
  };

  const updateUser = async (
    params: Partial<UserModel>
  ): Promise<{ success: boolean; message: string }> => {
    setSaving(true);
    if (!user || !getAvailableTypes().includes(params?.type as UserTypes)) {
      useToast("Utilisateur non connecté ou type non autorisé", {
        variant: "error",
      });
      return {
        success: false,
        message: "Utilisateur non connecté ou type non autorisé",
      };
    }

    const res = await usePutApi(
      `/auth/utilisateur/${getTypeToFetch()}`,
      params
    );

    const { success, message, data } = res ?? {};
    if (!success) {
      useToast(message ?? "Erreur lors de l'ajout de l'utilisateur", {
        variant: "error",
      });
      setSaving(false);
      return {
        success: false,
        message: message ?? "Erreur lors de l'ajout de l'utilisateur",
      };
    }

    useToast("Utilisateur ajouté avec succès", {
      variant: "success",
    });
    setSaving(false);
    return {
      success: true,
      message: "Utilisateur ajouté avec succès",
    };
  };

  const removeUser = async (params: {
    login: string;
    action: boolean;
  }): Promise<any> => {
    setRemoving(true);
    const res = await useDeleteApi(
      `/auth/utilisateur/${getTypeToFetch()}/${params.login}/${params.action}`
    );

    const { success, message } = res ?? {};
    if (!success) {
      useToast(message ?? "Erreur lors de la suppression de l'utilisateur", {
        variant: "error",
      });
      setRemoving(false);
      return {
        success: false,
        message: message ?? "Erreur lors de la suppression de l'utilisateur",
      };
    }

    useToast("Utilisateur supprimé avec succès", {
      variant: "success",
    });
    setRemoving(false);
    return {
      success: true,
      message: "Utilisateur supprimé avec succès",
    };
  };

  const getUserByMatricule = (matricule: string): UserModel | null => {
    return users.content.find((user) => user.matricule === matricule) || null;
  };

  return {
    user,
    open,
    users,
    getUserByMatricule,
    loading,
    isEditing,
    userTypesOptions: getUserTypesOptions(),
    availableTypes,
    saving,
    removing,
    saveUser,
    setOpen,
    setUsers,
    updateUser,
    fetchUsers,
    setIsEditing,
    setCurrentType,
    setAvailableTypes,
    getAvailableTypes,
    removeUser,
    getUsers,
    getTypeToFetch,
  };
};
